import { useEffect, useState } from 'react';
import type { Item } from '@slipway/shared';
import { humanSize, relativeTime } from '../lib/files.js';

export interface StreamProps {
  items: Item[];
  landingIds: Set<string>;
  onCopy: (item: Item) => void;
  onSave: (item: Item) => void;
  onOpen: (item: Item) => void;
}

/** Ticks over once a minute so relative timestamps stay honest without polling. */
function useNow(intervalMs = 30_000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
}

export function Stream({ items, landingIds, onCopy, onSave, onOpen }: StreamProps) {
  const now = useNow();

  if (items.length === 0) {
    return (
      <div className="stream" data-testid="stream">
        <div className="empty">
          <div className="big">Nothing here yet</div>
          <div className="hint">
            Send something from your iPhone, or press <span className="kbd">Ctrl</span>
            <span className="kbd">V</span> anywhere on this page to push your clipboard the other
            way.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="stream" data-testid="stream">
      {items.map((item, i) => {
        const isText = item.kind === 'text' || item.kind === 'link';
        const expired = item.state === 'expired';
        return (
          <article
            key={item.id}
            className={`row${landingIds.has(item.id) ? ' landing' : ''}${expired ? ' expired' : ''}`}
            data-kind={item.kind}
            data-testid="item"
          >
            <div className="tick">
              <span className="idx">{String(items.length - i).padStart(3, '0')}</span>
              <span className="dot" />
            </div>

            <div className="body">
              <div className="meta">
                <span>{item.kind}</span>
                <span className="sep">/</span>
                <span>{relativeTime(item.createdAt, now)}</span>
                {item.size !== null && (
                  <>
                    <span className="sep">/</span>
                    <span>{humanSize(item.size)}</span>
                  </>
                )}
                {expired && (
                  <>
                    <span className="sep">/</span>
                    <span>expired</span>
                  </>
                )}
              </div>

              {isText ? (
                item.kind === 'link' && !expired ? (
                  <a
                    className="content link"
                    href={item.inlineText ?? '#'}
                    target="_blank"
                    rel="noreferrer noopener"
                  >
                    {item.inlineText}
                  </a>
                ) : (
                  <div className="content">{item.inlineText ?? '—'}</div>
                )
              ) : (
                <div className="filename">{item.filename}</div>
              )}
            </div>

            <div className="actions">
              {expired ? (
                <span className="act" aria-disabled="true">
                  Upgrade to keep
                </span>
              ) : isText ? (
                <button className="act primary" onClick={() => onCopy(item)}>
                  Copy
                </button>
              ) : (
                <>
                  <button className="act primary" onClick={() => onSave(item)}>
                    Save
                  </button>
                  <button className="act" onClick={() => onOpen(item)}>
                    Open
                  </button>
                </>
              )}
            </div>
          </article>
        );
      })}
    </div>
  );
}
